import _ from 'lodash';
import React from 'react';
import debug from 'debug';
import { connect, ConnectedProps } from 'react-redux';
import { defer } from 'common/utils/helpers';
import { location } from 'common/utils/url-helper';
import { CARD_TYPE } from 'common/constants';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { CardData, StoryModel } from 'types/story';
import { AnswerDelta, updateStoryLastCardId } from 'client/utils';
import { sendGameCheckpoint } from 'client/actions/api';
import { ClientReducerState } from 'client/reducers';
import { CARD_ROOT_ID } from 'client/constants/common';
import { ISelectionHintContext } from 'client/components/common/SelectionHint/SelectionHintContext';
import InfoCard from 'client/components/common/StoryCard/Info';
import TriviaCard from 'client/components/common/StoryCard/Trivia';
import PollCard from 'client/components/common/StoryCard/Poll';
import PersonalityTestCard from 'client/components/common/StoryCard/PersonalityTest';
import TrueOrFalseCard from 'client/components/common/StoryCard/TrueOrFalse';
import ThisOrThatCard from 'client/components/common/StoryCard/ThisOrThat';
import NavigationCard from 'client/components/common/StoryCard/Navigation';
import FormCard from 'client/components/common/StoryCard/Form';
import RegistrationCard from 'client/components/common/StoryCard/Registration';
import SandboxCard from 'client/components/common/StoryCard/Sandbox';
import { ApiEvents } from 'client/ApiEvents';
import CardHeightObserver from './CardHeightObserver';
import css from './Card.scss';

const log = debug('client:components:Card');

const { isPreview } = location.client;

const mapState = (state: ClientReducerState) => ({
	organizationId: (state.story as StoryModel).organizationId,
	storyId: (state.story as StoryModel).id,
	user: state.user,
});

const connector = connect(mapState, { sendGameCheckpoint });

const MAP = {
	[CARD_TYPE.INFO]: InfoCard,
	[CARD_TYPE.TRIVIA]: TriviaCard,
	[CARD_TYPE.POLL]: PollCard,
	[CARD_TYPE.PERSONALITY_TEST]: PersonalityTestCard,
	[CARD_TYPE.TRUE_OR_FALSE]: TrueOrFalseCard,
	[CARD_TYPE.THIS_OR_THAT]: ThisOrThatCard,
	[CARD_TYPE.NAVIGATION]: NavigationCard,
	[CARD_TYPE.FORM]: FormCard,
	[CARD_TYPE.REGISTRATION]: RegistrationCard,
	[CARD_TYPE.SANDBOX]: SandboxCard,
};

const defaultProps = {
	isEditor: false,
	onCardClick: _.noop,
	children: [],
	nextDefaultCardId: '',
	organizationId: '',
	storyId: '',
};

type ReduxProps = ConnectedProps<typeof connector>;

type OwnProps = {
	card: CardData;
	nextDefaultCardId?: string;
	onCardClick?: (props: { organizationId: string; storyId: string; cardId: string }) => any;
	isEditor?: boolean;
	story: StoryFacade;
	treeOptions:
		| { target: 'default'; isCardTree: true }
		| ({ target: 'editor'; isCardTree: true } & ISelectionHintContext['editor']['treeOptions']);
};

type Props = ReduxProps & OwnProps & typeof defaultProps;

const events: {
	isValidProps: (props: Props) => boolean;
	getEventParams: (
		props: Props
	) => {
		organizationId: string;
		storyId: string;
		cardId: string;
		teamId: string;
		memberId: string;
	};
} = {
	isValidProps(props) {
		const { user, organizationId, storyId, card } = props;
		const hasData = organizationId && storyId && card._id;
		return !!hasData && !!user.isRegistered;
	},
	getEventParams(props) {
		return {
			organizationId: props.story.base.organizationId,
			storyId: props.story.storyId,
			cardId: props.card._id,
			teamId: props.story.base.teamId || '',
			memberId: props.story.base.createdBy,
		};
	},
};

class Card extends React.Component<Props> {
	static defaultProps = defaultProps;

	componentDidMount() {
		const cardId = this.props.card._id;
		log('didMount', cardId);

		this.setCardId();

		const isPlayedAlready = ApiEvents.viewedCards.includes(cardId); // handle history back case
		if (!this.props.isEditor && !isPreview && !isPlayedAlready) {
			updateStoryLastCardId(this.props.storyId, cardId);

			if (this.props.story.settings.restrictions?.oncePerUser) {
				this.props.sendGameCheckpoint(this.props.storyId, cardId);
			}
		}

		if (events.isValidProps(this.props)) {
			ApiEvents.sendCardViewEvent(events.getEventParams(this.props));
			this.sendFinishStoryEvent();
		}

		defer(() => {
			document.documentElement.scrollTop = 0;
		});
	}

	componentDidUpdate(prevProps: Props) {
		const { _id } = this.props.card;
		log('didUpdate', _id);

		if (_id !== prevProps.card._id) {
			this.setCardId();
		}

		if (events.isValidProps(this.props)) {
			ApiEvents.sendCardViewEvent(events.getEventParams(this.props));
			this.sendFinishStoryEvent();
		}
	}

	setCardId() {
		document.documentElement.dataset.cardId = this.props.card._id;

		const cardWithScore = [CARD_TYPE.TRIVIA, CARD_TYPE.PERSONALITY_TEST, CARD_TYPE.TRUE_OR_FALSE];

		if ((cardWithScore as CardData['type'][]).includes(this.props.card.type)) {
			AnswerDelta.start(this.props.card._id);
		}
	}

	sendFinishStoryEvent = () => {
		const isLastCard = !this.props.nextDefaultCardId;
		if (isLastCard) {
			ApiEvents.sendFinishStoryEvent(events.getEventParams(this.props));
		}
	};

	render() {
		const { card, nextDefaultCardId, isEditor, treeOptions } = this.props;
		const Component = MAP[card.type];

		if (isEditor && !this.props.treeOptions) return null;

		return (
			<div id={CARD_ROOT_ID} className={css.card} onClick={this.props.onCardClick}>
				<CardHeightObserver />
				<Component
					data={card}
					symbols={this.props.story.symbols}
					nextDefaultCardId={nextDefaultCardId}
					isEditor={isEditor}
					treeOptions={treeOptions}
				/>
				{this.props.children}
			</div>
		);
	}
}

export default connector(Card);
